import axios from 'axios';
import { API_BASE_URL } from './config';
import { getSelectedMid } from './businessUnits';

axios.defaults.baseURL = API_BASE_URL;

const USER_KEYS = [
    'isLoggedIn',
    'userAuthToken',
    'userSessionId',
    'userConnectionId',
    'userConnectionName',
    'userConnectionSyncStatus',
];

const ADMIN_KEYS = [
    'isAdminLoggedIn',
    'adminAuthToken',
    'adminSessionId',
];

const LEGACY_KEYS = [
    'authToken',
    'sessionId',
];

const PUBLIC_PATHS = ['/login', '/admin/login', '/forgot-password', '/reset-password'];

// Match the basename used by <BrowserRouter> in src/App.js so redirects and path
// checks work correctly when the app is mounted under a subpath (e.g. /auditHippo).
const BASENAME = (process.env.REACT_APP_BASENAME || process.env.PUBLIC_URL || '').replace(/\/$/, '');

function appPathname() {
    const p = window.location.pathname;
    if (BASENAME && p.startsWith(BASENAME)) {
        return p.slice(BASENAME.length) || '/';
    }
    return p;
}

let redirecting = false;

function clearUserAuth() {
    USER_KEYS.forEach(k => localStorage.removeItem(k));
    LEGACY_KEYS.forEach(k => localStorage.removeItem(k));
}

function clearAdminAuth() {
    ADMIN_KEYS.forEach(k => localStorage.removeItem(k));
    LEGACY_KEYS.forEach(k => localStorage.removeItem(k));
}

function isPublicPath(pathname) {
    return PUBLIC_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'));
}

function handleSessionEnd(reason, role) {
    if (redirecting) return;
    if (isPublicPath(appPathname())) {
        if (role === 'admin') clearAdminAuth();
        else clearUserAuth();
        return;
    }
    redirecting = true;
    if (role === 'admin') {
        clearAdminAuth();
    } else {
        clearUserAuth();
    }
    console.warn('[auth] session ended:', reason, '→ redirecting');
    const target = role === 'admin' ? '/admin/login' : '/login';
    window.location.replace(BASENAME + target);
}

function detectRole(error) {
    const url = error.config?.url || '';
    // Prefer the request URL: an admin endpoint always belongs to the admin session,
    // even if the user happens to be on a user page (and vice-versa).
    if (url.includes('/admin/')) return 'admin';
    if (url.includes('/auth/')   || url.includes('/user/')) return 'user';
    // Fall back to the page we're on.
    return appPathname().startsWith('/admin') ? 'admin' : 'user';
}

// Endpoints whose results belong to a single business unit. Listed as explicit
// prefixes rather than "everything under /rest/api/v1" so admin, auth and the
// BU list itself can never pick up a mid.
const MID_SCOPED_PREFIXES = [
    '/rest/sfmc/',                    // automations, journeys, data-extensions, users
    '/rest/api/v1/assets',            // cloud pages (list + detail)
    '/rest/api/v1/data-extensions',   // data extension detail
    '/rest/api/v1/email-studio',
    '/rest/api/v1/comparison',        // cross-BU summary (rows cover every BU regardless)
];

function isMidScoped(url) {
    const path = (url || '').split('?')[0];
    return MID_SCOPED_PREFIXES.some(prefix => path.startsWith(prefix));
}

function alreadyHasMid(config) {
    if (config.params && config.params.mid != null && config.params.mid !== '') return true;
    return /[?&]mid=/.test(config.url || '');
}

// Same heuristic as detectRole(error) but for request configs (before they're sent).
function detectRoleForRequest(config) {
    const url = config.url || '';
    if (url.includes('/admin/')) return 'admin';
    if (url.includes('/auth/')   || url.includes('/user/')) return 'user';
    return appPathname().startsWith('/admin') ? 'admin' : 'user';
}

// Auto-attach X-Token and X-Session-Id based on which session (user vs admin) the URL belongs to.
// Components shouldn't need to set these manually anymore.
axios.interceptors.request.use((config) => {
    const role  = detectRoleForRequest(config);
    const token = localStorage.getItem(role === 'admin' ? 'adminAuthToken' : 'userAuthToken');
    const sid   = localStorage.getItem(role === 'admin' ? 'adminSessionId' : 'userSessionId');
    config.headers = config.headers || {};
    if (token && !config.headers['X-Token'])      config.headers['X-Token']      = token;
    if (sid   && !config.headers['X-Session-Id']) config.headers['X-Session-Id'] = sid;

    // Scope page data to the business unit picked in the header switcher.
    // getSelectedMid() returns '' for the default BU, which the backend resolves
    // from the connection — so the mid param is simply left off there.
    if (role !== 'admin' && isMidScoped(config.url) && !alreadyHasMid(config)) {
        const mid = getSelectedMid();
        if (mid) config.params = { ...(config.params || {}), mid };
    }

    return config;
});

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;
        const data   = error.response?.data;
        const role   = detectRole(error);

        if (status === 401) {
            handleSessionEnd('401 unauthorized', role);
        } else if (status === 403 && data && data.connectionDeactivated === true) {
            // connectionDeactivated is a user-session concept; never log the admin out for it,
            // even if the failing call happened on a page where both sessions exist.
            if (role !== 'admin') {
                handleSessionEnd('403 connectionDeactivated', 'user');
            }
        }

        return Promise.reject(error);
    }
);

export { clearUserAuth, clearAdminAuth };
